import type { SelectedLine } from '../state/metroMapStore'
import type { SchemaData } from '../schema/types'
import { LineUrlKey } from '../lineView/LineUrlKey'
import type { SvgPathContains } from './svgPathContains'

/** `d` attributes of every `<path>` in a connection's pre-rendered svg. */
function pathData(svg: string): string[] {
  return [...svg.matchAll(/\sd="([^"]+)"/g)].map((match) => match[1])
}

/**
 * Finds the line whose segment lies under a map tap, for the line view's
 * "tap a line to open it". Only lines that have a line view (`hasLineView`)
 * are hit, and in night mode only connections still drawn (`drawnIds`).
 *
 * Two passes, so a tap lands on the line under the finger even where lines
 * run side by side (New York's trunk lines): first the connection whose
 * drawn shape contains the point, then — only if none does — the one within
 * `tolerance`. Within a pass the connection drawn last (on top) wins.
 *
 * @returns `(x, y, tolerance, drawnIds) → line | null` in schema canvas units.
 */
export function createLineSegmentHitTester(
  schema: SchemaData,
  hasLineView: (lineId: string) => boolean,
  contains: SvgPathContains,
): (
  x: number,
  y: number,
  tolerance: number,
  drawnIds: ReadonlySet<string> | null,
) => SelectedLine | null {
  const segments = schema.connections
    .filter((connection) => hasLineView(connection.lineId))
    .map((connection) => ({ connection, paths: pathData(connection.svgPath) }))
    .reverse()
  const find = (x: number, y: number, reach: number, drawnIds: ReadonlySet<string> | null) => {
    for (const { connection, paths } of segments) {
      if (drawnIds !== null && !drawnIds.has(connection.id)) continue
      if (paths.some((d) => contains(d, x, y, reach))) return connection.lineId
    }
    return null
  }
  return (x, y, tolerance, drawnIds) => {
    const lineId = find(x, y, 0, drawnIds) ?? find(x, y, tolerance, drawnIds)
    return lineId === null ? null : { lineId, slug: LineUrlKey.of(schema, lineId) }
  }
}
